import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tag,
  Tile,
} from '@carbon/react'
import { RecentlyViewed, CheckmarkFilled, CloseFilled } from '@carbon/icons-react'
import {
  formatAnalyticsPercentZeroDecimal,
  formatDateTimeFrShort,
  formatDurationMs,
  isAttemptPassedFromPercent,
} from '@sphere/shared/util'
import styles from './PersonalAnalytics.module.scss'

export interface RecentAttempt {
  event_id: string
  occurred_at: string
  activity_id: string
  activity_kind_code: string
  activity_title?: string
  duration_ms: number
  score_percent?: number
  pass?: boolean | null
}

export interface PersonalRecentAttemptsProps {
  attempts: RecentAttempt[]
}

const headers = [
  { key: 'date', header: 'Date' },
  { key: 'activity', header: 'Activité' },
  { key: 'type', header: 'Type' },
  { key: 'score', header: 'Score' },
  { key: 'pass', header: 'Résultat' },
  { key: 'duration', header: 'Durée' },
]

export function PersonalRecentAttempts({
  attempts,
}: PersonalRecentAttemptsProps) {
  const navigate = useNavigate()

  const resolveAttemptPass = (attempt: RecentAttempt): boolean | null =>
    typeof attempt.pass === 'boolean'
      ? attempt.pass
      : isAttemptPassedFromPercent(attempt.score_percent)

  const activityIdByEventId = React.useMemo(
    () => Object.fromEntries(attempts.map(a => [a.event_id, a.activity_id])),
    [attempts]
  )

  const passValueByEventId = React.useMemo(
    () =>
      Object.fromEntries(
        attempts.map(a => [a.event_id, resolveAttemptPass(a)])
      ),
    [attempts]
  )

  const rows = attempts.map(a => {
    const pass = resolveAttemptPass(a)
    return {
      id: a.event_id,
      date: formatDateTimeFrShort(a.occurred_at),
      activity: a.activity_title || a.activity_id,
      type: a.activity_kind_code,
      score:
        typeof a.score_percent === 'number'
          ? formatAnalyticsPercentZeroDecimal(a.score_percent)
          : '—',
      pass: pass === true ? 'Réussi' : pass === false ? 'Échoué' : '—',
      duration: formatDurationMs(a.duration_ms),
    }
  })

  return (
    <Tile className={styles.recentAttemptsTile}>
      <div className={styles.recentHeader}>
        <RecentlyViewed size={20} className={styles.recentHeaderIcon} />
        <h3 className={styles.sectionTitle} style={{ margin: 0 }}>
          Dernières tentatives
        </h3>
      </div>

      {attempts.length === 0 ? (
        <div className={styles.emptyState}>
          <p>Aucune tentative récente sur la période sélectionnée.</p>
        </div>
      ) : (
        <DataTable rows={rows} headers={headers} isSortable>
          {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
            <Table {...getTableProps()} size="md">
              <TableHead>
                <TableRow>
                  {headers.map(header => (
                    <TableHeader
                      key={header.key}
                      {...getHeaderProps({ header })}
                    >
                      {header.header}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => {
                  const activityId = activityIdByEventId[row.id]
                  const pass = passValueByEventId[row.id]
                  return (
                    <TableRow
                      key={row.id}
                      {...getRowProps({ row })}
                      className={activityId ? styles.clickableRow : undefined}
                      onClick={() =>
                        activityId &&
                        navigate(`/learning/activities/${activityId}`)
                      }
                    >
                      {row.cells.map(cell => {
                        if (cell.info.header === 'pass') {
                          return (
                            <TableCell key={cell.id}>
                              {pass === true ? (
                                <Tag
                                  type="green"
                                  renderIcon={CheckmarkFilled}
                                  size="sm"
                                >
                                  Réussi
                                </Tag>
                              ) : pass === false ? (
                                <Tag
                                  type="red"
                                  renderIcon={CloseFilled}
                                  size="sm"
                                >
                                  Échoué
                                </Tag>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                          )
                        }
                        return (
                          <TableCell key={cell.id}>{cell.value}</TableCell>
                        )
                      })}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </DataTable>
      )}
    </Tile>
  )
}
